/**
 * Authored V1 card pool (HEX-51 / ADR-0015 §8, §14, §15, §17).
 *
 * Pure data against the `Card` schema in `../types`. Adding a card is one new
 * `src/combat/cards/<type>/<slug>.ts` const plus one line in the matching array
 * below. The §14 worked cards (5 recursive + 5 trap) are encoded per the §17
 * mapping table and double as the canonical engine references.
 *
 * Pool tally — 32 gameplay cards (≥30 floor) plus 1 `die` pool-fuel card:
 *   attack 8 · defense 6 · support 7 · recursive 6 · trap 5
 *   rarity 12 common / 9 rare / 7 epic / 4 legendary
 * The §8 frame (30 cards, 11/9/6/4, recursive 5) is a starting point explicitly
 * open to rebalance; HEX-51 rebalanced to 11/9/7/4 (ADR-0015 §17). HEX-89 adds
 * Salve (common support heal) for HP-economy counter-pressure, lifting common
 * 11→12 and support 6→7 additively — the rarity floor is unchanged, only nudged
 * up. Recursive carries a 6th card (Spark, common) because the five mandated §14
 * recursive cards include no common, and the floor requires every type to have
 * one; the two §14 epic recursives lift epic to 7. Locked invariants — every
 * type ≥1 common, legendaries across attack/defense/recursive/trap (support
 * none, first-pass) — all hold.
 */

import type { Card, CardType } from '../types';

import { DOUBLE_STRIKE } from './attack/double-strike';
import { EXECUTIONER } from './attack/executioner';
import { HEAVY_CLEAVE } from './attack/heavy-cleave';
import { JAB } from './attack/jab';
import { POWER_STRIKE } from './attack/power-strike';
import { PRECISE_THRUST } from './attack/precise-thrust';
import { VENOMBLADE } from './attack/venomblade';
import { WORLDBREAKER } from './attack/worldbreaker';

import { AEGIS } from './defense/aegis';
import { BRACE } from './defense/brace';
import { BULWARK } from './defense/bulwark';
import { FORTRESS } from './defense/fortress';
import { GUARD } from './defense/guard';
import { SHIELD_UP } from './defense/shield-up';

import { ENFEEBLE } from './support/enfeeble';
import { HEX_WEAKEN } from './support/hex-weaken';
import { PANDEMIC } from './support/pandemic';
import { PLAGUE } from './support/plague';
import { REROLL } from './support/reroll';
import { SALVE } from './support/salve';
import { TOXIC_DART } from './support/toxic-dart';
import { VENOM } from './support/venom';

import { BLOODLETTING } from './recursive/bloodletting';
import { CHAOS_SPREAD } from './recursive/chaos-spread';
import { ECHOES } from './recursive/echoes';
import { ROULETTE_STRIKE } from './recursive/roulette-strike';
import { SPARK } from './recursive/spark';
import { WILD_LIGHTNING } from './recursive/wild-lightning';

import { CALTROPS } from './trap/caltrops';
import { COLD_SNAP } from './trap/cold-snap';
import { DOOMSDAY_CLOCK } from './trap/doomsday-clock';
import { MIRROR } from './trap/mirror';
import { SPIKE_WALL } from './trap/spike-wall';

import { EXTRA_DIE } from './die/extra-die';

export const ATTACK_CARDS: Card[] = [
  POWER_STRIKE,
  JAB,
  DOUBLE_STRIKE,
  HEAVY_CLEAVE,
  PRECISE_THRUST,
  VENOMBLADE,
  EXECUTIONER,
  WORLDBREAKER,
];

export const DEFENSE_CARDS: Card[] = [GUARD, SHIELD_UP, BRACE, BULWARK, AEGIS, FORTRESS];

export const SUPPORT_CARDS: Card[] = [
  VENOM,
  TOXIC_DART,
  HEX_WEAKEN,
  PLAGUE,
  ENFEEBLE,
  PANDEMIC,
  SALVE,
];

export const RECURSIVE_CARDS: Card[] = [
  SPARK,
  ROULETTE_STRIKE,
  WILD_LIGHTNING,
  BLOODLETTING,
  ECHOES,
  CHAOS_SPREAD,
];

export const TRAP_CARDS: Card[] = [COLD_SNAP, CALTROPS, SPIKE_WALL, MIRROR, DOOMSDAY_CLOCK];

/** Pool-fuel cards (CardType 'die'). Not part of the §8 30-card floor. */
export const DIE_CARDS: Card[] = [EXTRA_DIE];

/** Starter-only utility cards (ADR-0015 §19). Registered for `cardById` lookup +
 * the starter loadout, but — like `DIE_CARDS` — kept outside the §8 gameplay
 * floor and the type drop-pools, so the locked 11/9/7/4 tally is unaffected. */
export const STARTER_CARDS: Card[] = [REROLL];

/** The whole authored collection — gameplay cards plus die-fuel + starter cards. */
export const ALL_CARDS: Card[] = [
  ...ATTACK_CARDS,
  ...DEFENSE_CARDS,
  ...SUPPORT_CARDS,
  ...RECURSIVE_CARDS,
  ...TRAP_CARDS,
  ...DIE_CARDS,
  ...STARTER_CARDS,
];

/** Cards grouped by `type`, for tier-quality enemy composition and shop stock. */
export const CARDS_BY_TYPE: Record<CardType, Card[]> = {
  attack: ATTACK_CARDS,
  defense: DEFENSE_CARDS,
  support: SUPPORT_CARDS,
  recursive: RECURSIVE_CARDS,
  trap: TRAP_CARDS,
  die: DIE_CARDS,
};

const BY_ID: Map<string, Card> = new Map(ALL_CARDS.map((card) => [card.id, card]));

/** Look up an authored card by id. Throws on an unknown id — registry data is
 * trusted, so a miss is a wiring bug, not a runtime branch. */
export function cardById(id: string): Card {
  const card = BY_ID.get(id);
  if (card === undefined) throw new Error(`unknown card id: ${id}`);
  return card;
}

/** Starter pair (ADR-0015 §5): a common attack + a rare defense, both 2×1. The
 * genesis flow rolls the rare-defense slot from the rare defense subpool; Brace
 * is the first-pass pick (§15). */
export const STARTER_ATTACK: Card = POWER_STRIKE;
export const STARTER_DEFENSE: Card = BRACE;

/** Starter support (ADR-0015 §19): the free, once-per-turn Reroll rescue. Joins
 * the §5 attack/defense pair on the genesis loadout. */
export const STARTER_SUPPORT: Card = REROLL;
